<script setup>

import { usePage } from "@inertiajs/vue3";
import { Swiper, SwiperSlide } from 'swiper/vue';
import {ref, onMounted, onUnmounted, computed} from 'vue';
import { Pagination, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/mousewheel';
import App from '@/Layouts/App.vue';
import Button from '@/Components/Button.vue';
import imageHelper from '@/Helpers/image_helper.js';
import VisionSection from '@/Components/VisionSection.vue';
import AboutUsSection from '@/Components/Home/AboutUsSection.vue';
import SlideOurService from '@/Components/Home/SlideOurService.vue';
import OurProductCardSection from '@/Components/Home/OurProductCardSection.vue';
import OurServicesArticleSection from '@/Components/Home/OurServicesArticleSection.vue';

const { getImage } = imageHelper.methods;
const service = computed(() => usePage().props.service);
const ourVision = computed(() => usePage().props.ourVision);
const setting = computed(() => usePage().props.setting);

const props = defineProps({
    business: Array,
    resolution: Array,
    technologySolutions: Array,
    getProduct: Array,
});

const ourServicesSetting = computed(() => {
    return setting.value.filter(item => item.key === 'our_services');
});

const ourVisionSetting = computed(() => {
    return setting.value.filter(item => item.key === 'our_vision');
});

const serviceLength = computed(() => service.value.length < 4);

const swiperRef = ref(null);
const activeIndex = ref(0);
const totalSlides = computed(() => service.value.length);
const isMobile = ref(window.innerWidth < 800);

const checkScreenSize = () => {
    isMobile.value = window.innerWidth < 800;
};

onMounted(() => {
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkScreenSize);
});

const onSwiper = (swiper) => {
    swiperRef.value = swiper;
    swiper.on('slideChange', () => {
        activeIndex.value = swiper.activeIndex;
    });
};

const slidePrev = () => {
    if (swiperRef.value) {
        swiperRef.value.slidePrev();
    }
};

const slideNext = () => {
    if (swiperRef.value) {
        swiperRef.value.slideNext();
    }
};
</script>

<template>
    <App :isShowContactUs="true">
        <section class="content-banner-home bg-banner object-cover bg-center">
            <template v-for="item in business">
                <img class="absolute bottom-0 right-0"
                src="/assets/988tech/img/home/bg-hero.png"
                alt="Decorative Shape"
                >

                <div class="absolute top-0 w-full h-full">
                    <div class="content-body h-full grid-1-2-2 justify-center items-center content-padding">
                        <div
                            class="w-full h-auto flex flex-col justify-center items-center gap-y-2 md:gap-y-3 xl:justify-start xl:items-start pt-16 md:pt-0">
                            <h1 class="text-title text-center xl:text-start">{{ item.title }}</h1>
                            <p class="text-description text-grayBlackColor text-center xl:text-start"
                                v-html="item.description"></p>
                        </div>

                        <div class="w-full flex justify-center xl:justify-end items-center">
                            <img class="size-banner-hero-section"
                            :src="getImage(item.image_article)"
                            alt="bg header"
                            >
                        </div>
                    </div>
                </div>
            </template>
        </section>

        <section class="content-body content-padding">
            <AboutUsSection
            :resolution="resolution"
            />
        </section>

        <section class="w-full h-full flex flex-col justify-center items-center bg-grayWhiteColor py-5 md:py-10">
            <div class="content-body content-padding">
                <template v-for="item in ourServicesSetting">
                    <h1 class="text-title-content text-center mb-5 md:mb-10">{{ item.value }}</h1>
                </template>

                <!--== Desktop ==-->
                <template v-if="!isMobile">
                    <!--== Item < 3 ==-->
                    <template v-if="serviceLength">
                        <div class="w-full h-full grid grid-cols-1 md:grid-cols-3 gap-3 xl:gap-[20px]">
                            <template v-for="(item, index) in service" :key="index">
                                <OurServicesArticleSection
                                :id="item.id"
                                :bg="item.image_article"
                                :icon="item.image_thumbnail"
                                :title="item.title"
                                :description="item.excerpt"
                                :routeName="'our.service.detail'"
                                />
                            </template>
                        </div>
                    </template>
                    <!--== Large Item > 4 ==-->
                    <template v-if="!serviceLength">
                        <div class="relative w-full">
                            <button v-if="activeIndex > 0" @click="slidePrev"
                                class="absolute left-2 top-[50%] transform -translate-y-1/2 bg-white text-gray-700 w-10 h-10 rounded-full shadow-md z-10 hover:bg-gray-200 border border-gray-300">
                                <i class="ri-arrow-left-s-line text-2xl"></i>
                            </button>
                            <swiper
                            :slidesPerView="3" :spaceBetween="20"
                            :modules="[Mousewheel, Pagination]"
                            :mousewheel="{ forceToAxis: true }"
                            :speed="600"
                            @swiper="onSwiper"
                            class="mySwiper"
                            >
                                <swiper-slide v-for="(item, index) in service" :key="index">
                                    <SlideOurService
                                    :id="item.id"
                                    :bg="item.image_article"
                                    :icon="item.image_thumbnail"
                                    :title="item.title"
                                    :description="item.excerpt"
                                    :routeName="'our.service.detail'"
                                    />
                                </swiper-slide>
                            </swiper>
                            <button v-if="activeIndex < totalSlides - 3" @click="slideNext"
                                class="absolute right-2 top-[50%] transform -translate-y-1/2 bg-white text-gray-700 w-10 h-10 rounded-full shadow-md z-10 hover:bg-gray-200 border border-gray-300">
                                <i class="ri-arrow-right-s-line text-2xl"></i>
                            </button>
                        </div>
                    </template>
                </template>

                <!--== Mobile ==-->
                <template v-if="isMobile">
                    <div class="w-full h-full grid grid-cols-1 md:grid-cols-3 gap-3 xl:gap-[20px]">
                            <template v-for="(item, index) in service" :key="index">
                                <OurServicesArticleSection
                                :id="item.id"
                                :bg="item.image_article"
                                :icon="item.image_thumbnail"
                                :title="item.title"
                                :description="item.excerpt"
                                :routeName="'our.service.detail'"
                                />
                            </template>
                        </div>
                </template>
            </div>
        </section>

        <section class="content-body content-padding">
            <div class="grid grid-cols-1 md:grid-cols-2 py-5 md:py-10">
                <div class="flex flex-col justify-center md:justify-start items-center md:items-start gap-y-2 md:gap-y-3">
                    <template v-for="(item, index) in technologySolutions" :key="index">
                        <h1 class="text-title-content text-center md:text-start">{{ item.title }}</h1>
                        <p class="text-description text-center md:text-start">{{ item.description }}</p>
                    </template>

                    <div class="w-full grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-[20px] my-5">
                        <template v-for="(item, index) in getProduct" :key="index">
                            <OurProductCardSection
                            :title="item.title"
                            :icon="item.image_thumbnail"
                            />
                        </template>
                    </div>

                    <div class="w-full grid justify-center md:justify-end items-center md:items-start">
                        <Button class="block" :btnName="'អានលម្អិត'" :routeName="'our.product.detail'" />
                    </div>
                </div>

                <div class="w-full flex justify-center md:justify-end items-center md:items-center">
                    <template v-for="(item, index) in technologySolutions" :key="index">
                        <img class="size-banner-hero-section"
                        :src="getImage(item.image_article)"
                        alt="banner products"
                        >
                    </template>
                </div>
            </div>
        </section>

        <section class="w-full bg-grayWhiteColor">
            <div class="content-body content-padding py-5 md:py-10">
                <template v-for="item in ourVisionSetting">
                    <h1 class="text-title-content text-center mb-5 md:mb-10">{{ item.value }}</h1>
                </template>

                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 xl:gap-[20px]">
                    <template v-for="(item, index) in ourVision" :key="index">
                        <VisionSection
                        :title="item.title"
                        :description="item.description"
                        :index="index"
                        />
                    </template>
                </div>
            </div>
        </section>
    </App>
</template>
